import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';
import { Select } from './Input.js';

function pageWindow(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (page >= pageCount - 2) [pageCount - 1, pageCount - 2, pageCount - 3].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const result = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) result.push('…');
    result.push(p);
  });
  return result;
}

/**
 * Pagination
 * - page / pageCount / onPageChange are required.
 * - pageSize / onPageSizeChange render the "per page" selector.
 * - totalItems renders the "Showing x–y of n" summary.
 */
export default function Pagination({
  page,
  pageCount,
  onPageChange,
  pageSize,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
  totalItems,
  itemLabel = 'items',
  className,
  compact = false,
}) {
  const safeCount = Math.max(1, pageCount || 1);
  const safePage = Math.min(Math.max(1, page || 1), safeCount);
  const canPrev = safePage > 1;
  const canNext = safePage < safeCount;

  const from = totalItems != null && pageSize ? Math.min((safePage - 1) * pageSize + 1, totalItems) : null;
  const to = totalItems != null && pageSize ? Math.min(safePage * pageSize, totalItems) : null;

  const navButton = 'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md px-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between', className)}>
      <div className="flex items-center gap-3 text-sm text-gray-500">
        {totalItems != null && pageSize ? (
          <span>
            {totalItems === 0 ? (
              <>No {itemLabel}</>
            ) : (
              <>
                Showing <span className="font-medium text-gray-900">{from}</span>–<span className="font-medium text-gray-900">{to}</span> of{' '}
                <span className="font-medium text-gray-900">{totalItems}</span> {itemLabel}
              </>
            )}
          </span>
        ) : (
          <span>
            Page <span className="font-medium text-gray-900">{safePage}</span> of <span className="font-medium text-gray-900">{safeCount}</span>
          </span>
        )}
        {onPageSizeChange && (
          <Select
            size="sm"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Items per page"
            wrapperClassName="w-28"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt} / page
              </option>
            ))}
          </Select>
        )}
      </div>

      <nav className="flex items-center gap-1" aria-label="Pagination">
        <button type="button" className={cn(navButton, 'text-gray-500 hover:bg-gray-100 hover:text-gray-900')} onClick={() => onPageChange(safePage - 1)} disabled={!canPrev}>
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Previous</span>
        </button>
        {!compact &&
          pageWindow(safePage, safeCount).map((p, i) =>
            p === '…' ? (
              <span key={`e-${i}`} className="px-1 text-sm text-gray-400">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                aria-current={p === safePage ? 'page' : undefined}
                onClick={() => onPageChange(p)}
                className={cn(navButton, p === safePage ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')}
              >
                {p}
              </button>
            ),
          )}
        <button type="button" className={cn(navButton, 'text-gray-500 hover:bg-gray-100 hover:text-gray-900')} onClick={() => onPageChange(safePage + 1)} disabled={!canNext}>
          <span className="sr-only">Next</span>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}
